import Vue from 'vue'
import Vuex from 'vuex'

import { actionTypes, mutationTypes } from '@base/store/store-types'
import {
  baseState, baseGetters, baseMutations, baseActions,
  customerRequestInterceptor,
  customerResponseFulfilledInterceptor,
  customerResponseRejectedInterceptor,
  getToken,
  setToken
} from '@base/store'

import {
    userChangePassword,
    customerGet,
    updateCustomer,
    setTablePageSize,
    getUserMarket,
    getUserSaleLines,
    getUserProductSheets,
    createUserProductSheet,
    updateUserProductSheet,
    deleteUserProductSheet,
    getUserProducts,
    createUserProduct,
    userProductCampaign,
    getProductSaleLines,
    getUserSaleLinesSummary,
    getPaymentReports,
    getShelfPeriodPrices,
    getShelfTypes,
    getShelfTypeServices,
    getAllBookings,
    getShelfTypeAvailability,
    getBookingRequest,
    bookingRequestReserve,
    bookingRequestDelete,
    bookingRequestInitPayment,
    bookingRequestConfirmPayment,
    bookingRequestBookWithoutPayment,
    bookingPaymentTransaction,
    switchCustomerAccount,
    getCustomerAccounts,
} from '@user/services'
import { webConfig, DEFAULT_PAGE_SIZE } from '@base/configs'
import { cleanStorages } from '@base/store'
import { revokeToken } from '@base/services'
import i18n from '@base/i18n'

Vue.use(Vuex)

const initialState = {
  ...baseState,

  customerData: {},
  customerAccounts: [],
  userNavItems: [],
  userMarket: null,
  userSaleLineSummary: [],
  userSaleLines: {},
  userSaleLinesArg: {
    filters: null
  },
  productSaleLines: [],
  selectedProductSheet: null,
  bookings: [],
  bookingsArg: {
    filters: null,
    page: 1,
    pagesCount: 0,
    count: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  selectedBookingRequest: {},
  shelfTypes: [],
  shelfPeriodPrices: [],
  shelfTypeServices: [],
  shelfTypeAvailability: null,
  userProductSheets: [],
  userProductSheetsArg: {
    filters: null,
    page: 1,
    pagesCount: 0,
    count: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  userProducts: [],
  userProductsArg: {
    filters: null,
    page: 1,
    pagesCount: 0,
    count: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  paymentReports: [],
  paymentReportsArg: {
    page: 1,
    pagesCount: 0,
    count: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    filters: null
  },
}

const axiosInstances = [
  userChangePassword,
  customerGet,
  updateCustomer,
  setTablePageSize,
  getUserMarket,
  getUserSaleLines,
  getUserSaleLinesSummary,
  getUserProductSheets,
  createUserProductSheet,
  updateUserProductSheet,
  deleteUserProductSheet,
  getUserProducts,
  createUserProduct,
  userProductCampaign,
  getProductSaleLines,
  getPaymentReports,
  getAllBookings,
  getShelfPeriodPrices,
  getShelfTypes,
  getShelfTypeServices,
  getShelfTypeAvailability,
  getBookingRequest,
  bookingRequestReserve,
  bookingRequestInitPayment,
  bookingRequestConfirmPayment,
  bookingRequestBookWithoutPayment,
  bookingRequestDelete,
  bookingPaymentTransaction,
  switchCustomerAccount,
  getCustomerAccounts,
]

for (const instance of axiosInstances) {
  instance.interceptors.request.use(customerRequestInterceptor)
  instance.interceptors.response.use(
    customerResponseFulfilledInterceptor,
    customerResponseRejectedInterceptor
  )
}

document.initialState = initialState      // Need in each section, stores for base logout user behavior

export default new Vuex.Store({
  state: initialState,
  getters: {
    ...baseGetters,

    getUserProductSheetsPage: state => state.userProductSheetsArg.page,
    getUserProductSheetsPageSize: state => state.userProductSheetsArg.pageSize,
    getUserProductSheetsPagesCount: state => state.userProductSheetsArg.pagesCount,
    getUserProductSheetsCount: state => state.userProductSheetsArg.count,

    getUserProductsPage: state => state.userProductsArg.page,
    getUserProductsPageSize: state => state.userProductsArg.pageSize,
    getUserProductsPagesCount: state => state.userProductsArg.pagesCount,
    getUserProductsCount: state => state.userProductsArg.count,

    getPaymentReportPage: state => state.paymentReportsArg.page,
    getPaymentReportPageSize: state => state.paymentReportsArg.pageSize,
    getPaymentReportPagesCount: state => state.paymentReportsArg.pagesCount,
    getPaymentReportCount: state => state.paymentReportsArg.count,

    getBookingsPage: state => state.bookingsArg.page,
    getBookingsPageSize: state => state.bookingsArg.pageSize,
    getBookingsPagesCount: state => state.bookingsArg.pagesCount,
    getBookingsCount: state => state.bookingsArg.count,

  },

  mutations: {
    ...baseMutations,

    [mutationTypes.SET_USER_MARKET](state, market) {
      state.userMarket = market
    },
    [mutationTypes.SET_CUSTOMER_DATA](state, data) {
      state.customerData = data
    },
    [mutationTypes.SET_CAMPAIGN_REQUEST](state, campaign) {
      state.customerData.campaignRequestName = campaign.name
      state.customerData.campaignRequestDiscount = campaign.discountPercent
    },
    [mutationTypes.LOGOUT_CUSTOMER](state, url) {
      state = document.initialState
      cleanStorages('CustomerToken')
      document.location = url ? url : webConfig.baseURL
    },

    [mutationTypes.SET_USER_NAV_ITEMS](state) {
      state.userNavItems = [
        {
          href: { name: 'sales' },
          text: i18n.t('navigation.customer.sales')
        },
        {
          href: { name: 'settings' },
          text: i18n.t('navigation.general.settings')
        },
        {
          href: '#',
          text: i18n.t('navigation.general.logout')
        }
      ]

      const token = getToken('CustomerToken')
      if (token && (token.isFlealoverSoloFeature || token.flexposApiValid || (token.zettleIntegration && token.zettleApiValid)) && token.individualizedItemsAccess) {
        if (token.customerAccessToProductCreation) {
          state.userNavItems.splice(1, 0, {
            href: { name: 'product-sheets' },
            text: i18n.t('navigation.customer.product_sheets')
          })
        }
        state.userNavItems.splice(1, 0, {
          href: { name: 'products' },
          text: i18n.t('navigation.customer.products')
        })
      }
      if (token && token.paymentsFeature) {
        state.userNavItems.splice(1, 0, {
          href: { name: 'payments' },
          text: i18n.t('navigation.customer.payments')
        },)
      }
      if (token && token.onlineBookingFeature && (token.customerAccessToBooking || token.customerAccessToBookingPaymentRequests) && (token.gatewayApiValid || token.allowOfflinePayments) && token.showPaymentTerms) {
        state.userNavItems.splice(2, 0, {
          href: { name: 'bookings' },
          text: i18n.t('navigation.customer.booking_requests')
        },)
      }
    },
    [mutationTypes.SET_SALE_LINES_LIST] (state, lines) {
      state.userSaleLines = lines
    },
    [mutationTypes.SET_SALE_LINES_SUMMARY] (state, summary) {
      state.userSaleLineSummary = summary
    },
    [mutationTypes.SET_SALE_LINES_FILTERS] (state, filters) {
      state.userSaleLinesArg.filters = filters
    },
    [mutationTypes.SET_PRODUCT_SHEET_LIST] (state, sheets) {
      state.userProductSheets = sheets
    },
    [mutationTypes.SET_PRODUCT_SHEET] (state, sheet) {
      state.selectedProductSheet = sheet
      if (sheet) {
        const token = getToken('CustomerToken')
        if (token.zettleIntegration) {
          state.selectedProductSheet.products = sheet.zettleProducts
        } else if (token.isFlealoverSoloFeature) {
          state.selectedProductSheet.products = sheet.soloProducts || []
        } else {
          state.selectedProductSheet.products = sheet.flexposProducts
        }
      }
    },
    [mutationTypes.SET_PRODUCT_SALE_LINE_LIST] (state, lines) {
      state.productSaleLines = lines
    },
    [mutationTypes.SET_PRODUCT_SHEET_FILTERS] (state, filters) {
      state.userProductSheetsArg.filters = filters
    },
    [mutationTypes.SET_PRODUCT_SHEET_PAGE] (state, page) {
      state.userProductSheetsArg.page = page
    },
    [mutationTypes.SET_PRODUCT_SHEET_PAGES_COUNT] (state, count) {
      state.userProductSheetsArg.pagesCount = count
    },
    [mutationTypes.SET_PRODUCT_SHEET_COUNT] (state, count) {
      state.userProductSheetsArg.count = count
    },
    [mutationTypes.APPEND_PRODUCT_SHEET](state, sheet) {
      state.userProductSheets.unshift(sheet)
    },
    [mutationTypes.DELETE_PRODUCT_SHEET](state, id) {
      const oldProductSheetIndex = state.userProductSheets.findIndex(sheet => sheet.id === id)
      if (oldProductSheetIndex >= 0)
        state.userProductSheets.splice(oldProductSheetIndex, 1)
    },
    [mutationTypes.UPDATE_PRODUCT_SHEET](state, { id, sheet }) {
      const oldProductSheetObj = state.userProductSheets.find(obj => obj.id === id)
      Object.assign(oldProductSheetObj, sheet)
    },
    [mutationTypes.UPDATE_SELECTED_PRODUCT_SHEET](state, sheet) {
      state.selectedProductSheet = sheet
      const token = getToken('CustomerToken')
      if (sheet) {
        if (token.zettleIntegration) {
          state.selectedProductSheet.products = sheet.zettleProducts
        } else if (token.isFlealoverSoloFeature) {
          state.selectedProductSheet.products = sheet.soloProducts || []
        } else {
          state.selectedProductSheet.products = sheet.flexposProducts
        }
      }
    },
    [mutationTypes.SET_PRODUCT_LIST] (state, products) {
      state.userProducts = products
    },
    [mutationTypes.SET_PRODUCT_FILTERS] (state, filters) {
      state.userProductsArg.filters = filters
    },
    [mutationTypes.SET_PRODUCT_PAGE] (state, page) {
      state.userProductsArg.page = page
    },
    [mutationTypes.SET_PRODUCT_PAGES_COUNT] (state, count) {
      state.userProductsArg.pagesCount = count
    },
    [mutationTypes.SET_PRODUCT_COUNT] (state, count) {
      state.userProductsArg.count = count
    },
    [mutationTypes.SET_PAYMENT_REPORTS](state, reports) {
      state.paymentReports = reports
    },
    [mutationTypes.SET_PAYMENT_REPORTS_FILTERS] (state, filters) {
      state.paymentReportsArg.filters = filters
    },
    [mutationTypes.SET_PAYMENT_REPORTS_PAGE] (state, page) {
      state.paymentReportsArg.page = page
    },
    [mutationTypes.SET_PAYMENT_REPORTS_PAGES_COUNT] (state, count) {
      state.paymentReportsArg.pagesCount = count
    },
    [mutationTypes.SET_PAYMENT_REPORTS_COUNT] (state, count) {
      state.paymentReportsArg.count = count
    },
    [mutationTypes.SET_BOOKING_REQUEST] (state, data) {
      state.selectedBookingRequest = data
    },
    [mutationTypes.SET_BOOKINGS_LIST] (state, data) {
      state.bookings = data
    },
    [mutationTypes.SET_BOOKINGS_FILTERS] (state, filters) {
      state.bookingsArg.filters = filters
    },
    [mutationTypes.SET_BOOKINGS_PAGE] (state, page) {
      state.bookingsArg.page = page
    },
    [mutationTypes.SET_BOOKINGS_PAGES_COUNT] (state, count) {
      state.bookingsArg.pagesCount = count
    },
    [mutationTypes.SET_BOOKINGS_COUNT] (state, count) {
      state.bookingsArg.count = count
    },
    [mutationTypes.SET_SHELF_TYPE_SERVICES](state, services) {
      state.shelfTypeServices = services
    },
    [mutationTypes.SET_SHELF_TYPES](state, types) {
      state.shelfTypes = types
    },
    [mutationTypes.SET_SHELF_PERIOD_PRICES](state, prices) {
      state.shelfPeriodPrices = prices
    },
    [mutationTypes.SET_SHELF_TYPE_AVAILABILITY](state, data) {
      state.shelfTypeAvailability = data
    },
    [mutationTypes.SET_CUSTOMER_ACCOUNTS](state, accounts) {
      state.customerAccounts = accounts
    }
  },

  actions: {
    ...baseActions,

    [actionTypes.CHANGE_TABLE_PAGE_SIZE]({ commit }, size) {
      commit(mutationTypes.SET_TABLE_PAGE_SIZE, size)
      const token = getToken('CustomerToken')
      if (token) {
        token.tablePageSize = size
        setToken(token)
      }
      // Persisting the preference is best-effort - a failure must not block the table.
      return setTablePageSize({ data: { pageSize: size } }).catch(() => {})
    },
    [actionTypes.GET_CUSTOMER_DATA]({ commit }) {
      return new Promise((resolve, reject) => {
        customerGet()
          .then((response) => {
            commit(mutationTypes.SET_USER_DATA, response.data)
            commit(mutationTypes.SET_CUSTOMER_DATA, response.data)
            commit(mutationTypes.SET_TABLE_PAGE_SIZE, response.data.tablePageSize || DEFAULT_PAGE_SIZE)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.CHANGE_CUSTOMER_DATA] ({commit}, payloads) {
      return new Promise((resolve, reject) => {
        updateCustomer({
          data: payloads
        })
          .then((response) => {
            const token = getToken('CustomerToken')
            if (token) {
              token.username = response.data.username
              setToken(token)
              commit(mutationTypes.SET_USER_DATA, response.data)
            } else {
              commit(mutationTypes.LOGOUT_CUSTOMER)
            }
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.LOGOUT_CUSTOMER]({ commit }, url) {
      return new Promise((resolve, reject) => {
        const token = getToken('CustomerToken')
        if (!token || !token.accessToken) {
          commit(mutationTypes.LOGOUT_CUSTOMER)
        }
        revokeToken({
          data: {'token': token.accessToken}
        })
          .then(response => {
            commit(mutationTypes.LOGOUT_CUSTOMER, url)
            resolve(response)
          })
          .catch(error => {
            commit(mutationTypes.LOGOUT_CUSTOMER)
            reject(error.response)
          })
      })
    },

    [actionTypes.GET_USER_MARKET]({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        getUserMarket()
          .then(response => {
            if (response.data.marketId) {
              const token = getToken('CustomerToken')
              if (token) {
                token.flexposApiValid = response.data.flexposApiValid
                token.zettleApiValid = response.data.zettleApiValid
                token.zettleIntegration = response.data.zettleIntegration
                token.individualizedItemsAccess = response.data.individualizedItemsAccess
                token.customerGenericProductsCreation = response.data.customerGenericProductsCreation
                token.customerAccessToProductCreation = response.data.customerAccessToProductCreation
                token.customerAccessToProductCompletion = response.data.customerAccessToProductCompletion
                token.customerAccessToAccountBalance = response.data.customerAccessToAccountBalance
                token.customerAccessToBooking = response.data.customerAccessToBooking
                token.onlineBookingFeature = response.data.onlineBookingFeature
                token.subaccountFeature = response.data.subaccountFeature
                token.gatewayApiValid = response.data.gatewayApiValid
                token.allowOnlinePayments = response.data.allowOnlinePayments
                token.allowOfflinePayments = response.data.allowOfflinePayments
                token.showPaymentTerms = response.data.showPaymentTerms
                setToken(token)
                commit(mutationTypes.SET_USER_NAV_ITEMS)
              } else {
                dispatch(actionTypes.LOGOUT_CUSTOMER)
              }
              commit(mutationTypes.SET_USER_MARKET, response.data)
              resolve(response.data)
            } else {
              commit(mutationTypes.LOGOUT_CUSTOMER)
            }
          })
          .catch((error) => {
            reject(error.response)
          })
      })
    },

    [actionTypes.GET_SALE_LINES_LIST]({ commit, state }) {
      return new Promise((resolve, reject) => {
        getUserSaleLines({
          params: state.userSaleLinesArg.filters
        })
          .then(response => {
            commit(mutationTypes.SET_SALE_LINES_LIST, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SALE_LINES_SUMMARY]({ commit }) {
      return new Promise((resolve, reject) => {
        getUserSaleLinesSummary()
          .then(response => {
            commit(mutationTypes.SET_SALE_LINES_SUMMARY, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.GET_USER_PRODUCT_SHEET_LIST]({ commit, state }) {
      return new Promise((resolve, reject) => {
        getUserProductSheets({
          params: {
            page: state.userProductSheetsArg.page,
            pageSize: state.userProductSheetsArg.pageSize,
            ...state.userProductSheetsArg.filters
          }
        })
          .then(response => {
            commit(mutationTypes.SET_PRODUCT_SHEET_LIST, response.data.results)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_USER_PRODUCT_SHEET]({ commit }, id) {
      return new Promise((resolve, reject) => {
        getUserProductSheets({
          url: `${id}/`
        })
          .then(response => {
            commit(mutationTypes.SET_PRODUCT_SHEET, response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_PRODUCT_SALE_LINE_LIST]({ commit }, id) {
      return new Promise((resolve, reject) => {
        getProductSaleLines({
          url: `${id}/`
        })
          .then(response => {
            commit(mutationTypes.SET_PRODUCT_SALE_LINE_LIST, response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.CREATE_USER_PRODUCT_SHEET]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        createUserProductSheet({
          data: payloads
        })
          .then(response => {
            commit(mutationTypes.APPEND_PRODUCT_SHEET, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.UPDATE_USER_PRODUCT_SHEET]({ commit }, { payloads, id }) {
      return new Promise((resolve, reject) => {
        updateUserProductSheet({
          url: `${id}/`,
          data: payloads
        })
          .then(response => {
            commit(mutationTypes.UPDATE_SELECTED_PRODUCT_SHEET, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.COMPLETE_USER_PRODUCT_SHEET]({ commit }, id) {
      return new Promise((resolve, reject) => {
        updateUserProductSheet({
          url: `${id}/complete/`,
        })
          .then(response => {
            commit(mutationTypes.UPDATE_PRODUCT_SHEET, { id: id, sheet: response.data })
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.PRINT_USER_PRODUCT_SHEET]({ commit }, id) {
      return new Promise((resolve, reject) => {
        updateUserProductSheet({
          url: `${id}/print/`,
        })
          .then(response => {
            commit(mutationTypes.UPDATE_PRODUCT_SHEET, { id: id, sheet: response.data })
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.DELETE_USER_PRODUCT_SHEET]({ commit }, id) {
      return new Promise((resolve, reject) => {
        deleteUserProductSheet({
          url: `${id}/`
        })
          .then(response => {
            commit(mutationTypes.DELETE_PRODUCT_SHEET, id)
            resolve(response)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.GET_USER_PRODUCT_LIST]({ commit, state }) {
      return new Promise((resolve, reject) => {
        getUserProducts({
          params: {
            page: state.userProductsArg.page,
            pageSize: state.userProductsArg.pageSize,
            ...state.userProductsArg.filters
          }
        })
          .then(response => {
            commit(mutationTypes.SET_PRODUCT_LIST, response.data.results)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.CREATE_USER_PRODUCTS]({ commit }, { productSheetId, products }) {
      return new Promise((resolve, reject) => {
        createUserProduct({
          url: `${productSheetId}/create_for_product_sheet/`,
          data: products
        })
          .then(response => {
            commit(mutationTypes.SET_PRODUCT_LIST, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.REQUEST_PRODUCT_CAMPAIGN]({ commit }, {userNumber, id}) {
      return new Promise((resolve, reject) => {
        userProductCampaign({
          url: `${userNumber}/${id}/add_request/`
        })
          .then(response => {
            commit(mutationTypes.SET_CAMPAIGN_REQUEST, response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.REQUEST_PRODUCT_CAMPAIGN_REMOVAL]({ commit }, userNumber) {
      return new Promise((resolve, reject) => {
        userProductCampaign({
          url: `${userNumber}/remove_request/`
        })
          .then(response => {
            commit(mutationTypes.SET_CAMPAIGN_REQUEST, response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.GET_PAYMENT_REPORTS]({ commit, state }) {
      return new Promise((resolve, reject) => {
        getPaymentReports({
          params: {
            page: state.paymentReportsArg.page,
            pageSize: state.paymentReportsArg.pageSize,
            ...state.paymentReportsArg.filters
          }
        })
          .then(response => {
            commit(mutationTypes.SET_PAYMENT_REPORTS, response.data.results)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.DOWNLOAD_PAYMENT_REPORT_PDF]({ commit }, id) {
      return new Promise((resolve, reject) => {
        getPaymentReports({
          method: 'post',
          url: `${id}/download_pdf/`,
        })
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.GET_ALL_CUSTOMER_BOOKINGS]({ commit, state }) {
      return new Promise((resolve, reject) => {
        getAllBookings({
          params: {
            page: state.bookingsArg.page,
            pageSize: state.bookingsArg.pageSize,
            ...state.bookingsArg.filters
          }
        })
          .then(response => {
            commit(mutationTypes.SET_BOOKINGS_LIST, response.data.results)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SHELF_TYPE_SERVICES]({ commit }, shelfTypeId) {
      return new Promise((resolve, reject) => {
        getShelfTypeServices({
          url: `${shelfTypeId}/`
        })
          .then(response => {
            commit(mutationTypes.SET_SHELF_TYPE_SERVICES, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SHELF_TYPES]({ commit }) {
      return new Promise((resolve, reject) => {
        getShelfTypes()
          .then(response => {
            commit(mutationTypes.SET_SHELF_TYPES, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SHELF_PERIOD_PRICES]({ commit }, shelfTypeId) {
      return new Promise((resolve, reject) => {
        getShelfPeriodPrices({
          url: `${shelfTypeId}/`
        })
          .then(response => {
            commit(mutationTypes.SET_SHELF_PERIOD_PRICES, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SHELF_TYPE_AVAILABILITY]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        getShelfTypeAvailability({
          params: payload,
        })
          .then(response => {
            commit(mutationTypes.SET_SHELF_TYPE_AVAILABILITY, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_BOOKING_REQUEST]({ commit }, bookingRequestId) {
      return new Promise((resolve, reject) => {
        getBookingRequest({
          url: `${bookingRequestId}/`,
        })
          .then(response => {
            commit(mutationTypes.SET_BOOKING_REQUEST, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_RESERVE]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequestReserve({
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_INIT_PAYMENT]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequestInitPayment({
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_CONFIRM_PAYMENT]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequestConfirmPayment({
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_BOOK_WITHOUT_PAYMENT]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequestBookWithoutPayment({
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_DELETE]({ commit }, bookingRequestId) {
      return new Promise((resolve, reject) => {
        bookingRequestDelete({
          url: `${bookingRequestId}/`,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_PAYMENT_TRANSACTION_FAIL]({ commit }, bookingPaymentTxId) {
      return new Promise((resolve, reject) => {
        bookingPaymentTransaction({
          url: `${bookingPaymentTxId}/fail/`,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_PAYMENT_TRANSACTION_CANCEL]({ commit }, bookingPaymentTxId) {
      return new Promise((resolve, reject) => {
        bookingPaymentTransaction({
          url: `${bookingPaymentTxId}/cancel/`,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.SWITCH_CUSTOMER_ACCOUNT]({ commit }, accountUserNumber) {
      return new Promise((resolve, reject) => {
        switchCustomerAccount({
            url: `${accountUserNumber}/`,
        })
          .then((response) => {
            setToken(response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_CUSTOMER_ACCOUNTS]({ commit }) {
      return new Promise((resolve, reject) => {
        getCustomerAccounts()
          .then((response) => {
            commit(mutationTypes.SET_CUSTOMER_ACCOUNTS, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    }
  }
})
